import { useEffect, useRef, useState } from 'react';
import './NHIPage.css';

import Slide1 from '../../assets/NHIPAGE/slide-01.webp';
import Slide2 from '../../assets/NHIPAGE/slide-02.webp';
import Slide3 from '../../assets/NHIPAGE/slide-03.webp';
import Slide4 from '../../assets/NHIPAGE/slide-04.webp';
import Slide5 from '../../assets/NHIPAGE/slide-05.webp';
import Slide6 from '../../assets/NHIPAGE/slide-06.webp';
import Slide7 from '../../assets/NHIPAGE/slide-07.webp';
import Slide8 from '../../assets/NHIPAGE/slide-08.webp';
import Slide9 from '../../assets/NHIPAGE/slide-09.webp';
import Slide10 from '../../assets/NHIPAGE/slide-10.webp';
import Slide11 from '../../assets/NHIPAGE/slide-11.webp';
import Slide12 from '../../assets/NHIPAGE/slide-12.webp';
import Slide13 from '../../assets/NHIPAGE/slide-13.webp';
import Slide14 from '../../assets/NHIPAGE/slide-14.webp';

const slides = [
  {
    image: Slide1,
    title: 'The Big Shift to Natural Human Intelligence',
  },
  {
    image: Slide2,
    title: 'The automation paradox and the exhaustion of the sleeping mode',
  },
  {
    image: Slide3,
    title: 'Reclaiming the sovereign dignity of the human person',
  },
  {
    image: Slide4,
    title:
      'A permanent shift from artificial acceleration to natural restoration',
  },
  {
    image: Slide5,
    title: 'Why Symphonia? The architecture of perfect tension',
  },
  {
    image: Slide6,
    title: 'Biological resilience outlasts the Digital Pleistocene',
  },
  {
    image: Slide7,
    title: 'Organizing the enterprise to carry the human voice',
  },
  {
    image: Slide8,
    title: "Ethical governance as the music's written form",
  },
  {
    image: Slide9,
    title: 'The death of Minimalist Modernism',
  },
  {
    image: Slide10,
    title: 'Waking the sleeping market with a whisper, not a shout',
  },
  {
    image: Slide11,
    title: 'The Three Movements of the Big Shift',
  },
  {
    image: Slide12,
    title: 'The Launch Protocol: Plan, Define, Deliver',
  },
  {
    image: Slide13,
    title: 'Commercial Implementation: Today, not tomorrow',
  },
  {
    image: Slide14,
    title: 'Leave the machine. Return to your image',
  },
];

const sections = [
  {
    id: 'foundation',
    number: '01',
    eyebrow: 'The Foundation',
    title: 'The Big Shift to Natural Human Intelligence',
    description:
      'The transition from machine-centered acceleration toward restored human origin, dignity, belonging, and discernment.',
    slideIndexes: [0, 1, 2],
    featuredSlide: 0,
  },
  {
    id: 'restoration',
    number: '02',
    eyebrow: 'The Ontological Pivot',
    title: 'From Artificial Acceleration to Natural Restoration',
    description:
      'Technology returns to its proper role as an instrument while the human person returns to the center.',
    slideIndexes: [3, 4, 5],
    featuredSlide: 3,
  },
  {
    id: 'governance',
    number: '03',
    eyebrow: 'The Human-Led Enterprise',
    title: 'Enterprise Architecture and Ethical Governance',
    description:
      'An organizational model where technology, authority, regulation, and finance exist to serve and elevate the human voice.',
    slideIndexes: [6, 7],
  },
  {
    id: 'humanism',
    number: '04',
    eyebrow: 'The New Movement',
    title: 'Expressive Humanism and Market Positioning',
    description:
      'A movement away from sterile machine-centered design toward warmth, meaning, imperfection, and human resonance.',
    slideIndexes: [8, 9],
  },
  {
    id: 'implementation',
    number: '05',
    eyebrow: 'From Vision to Action',
    title: 'The Movements and Implementation of the Big Shift',
    description:
      'A practical path from awakening and restoration to commercial implementation and human-led intelligence.',
    slideIndexes: [10, 11, 12, 13],
    featuredSlide: 13,
  },
];

export default function NHIPage() {
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visibleEntry) return;

        const sectionIndex = Number(
          visibleEntry.target.dataset.sectionIndex,
        );

        setActiveSection(sectionIndex);
      },
      {
        threshold: [0.15, 0.3, 0.5],
        rootMargin: '-15% 0px -35% 0px',
      },
    );

    sectionRefs.current.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const goToSection = (index) => {
    sectionRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <main className="nhi-page">
      <nav
        className="nhi-page__nav"
        aria-label="Natural Human Intelligence sections"
      >
        {sections.map((section, index) => (
          <button
            key={section.id}
            type="button"
            className={`nhi-page__nav-button ${activeSection === index ? 'is-active' : ''
              }`}
            onClick={() => goToSection(index)}
            aria-label={`Open section ${section.number}: ${section.title}`}
            aria-current={
              activeSection === index ? 'true' : undefined
            }
          >
            <span>{section.number}</span>
          </button>
        ))}
      </nav>

      {sections.map((section, sectionIndex) => (
        <section
          key={section.id}
          id={section.id}
          ref={(element) => {
            sectionRefs.current[sectionIndex] = element;
          }}
          className="nhi-section"
          data-section-index={sectionIndex}
          aria-labelledby={`${section.id}-title`}
        >
          <div className="nhi-section__container">
            <header className="nhi-section__header">
              <div className="nhi-section__label">
                <span>{section.number}</span>
                <p>{section.eyebrow}</p>
              </div>

              <div className="nhi-section__heading">
                <h2 id={`${section.id}-title`}>
                  {section.title}
                </h2>

                <p>{section.description}</p>
              </div>
            </header>

            <div
              className={`nhi-section__slides ${section.slideIndexes.length === 2
                  ? 'nhi-section__slides--two'
                  : ''
                }`}
            >
              {section.slideIndexes.map((slideIndex) => {
                const slide = slides[slideIndex];

                const isFeatured =
                  section.featuredSlide === slideIndex;

                return (
                  <figure
                    key={slide.image}
                    className={`nhi-slide-card ${isFeatured
                        ? 'nhi-slide-card--featured'
                        : ''
                      }`}
                  >
                    <div className="nhi-slide-card__frame">
                      <img
                        src={slide.image}
                        alt={`Slide ${slideIndex + 1}: ${slide.title
                          }`}
                        loading={slideIndex === 0 ? 'eager' : 'lazy'}
                        fetchPriority={
                          slideIndex === 0 ? 'high' : 'auto'
                        }
                        decoding="async"
                      />
                    </div>

                    <figcaption>
                      <span>
                        {String(slideIndex + 1).padStart(2, '0')}
                      </span>

                      <p>{slide.title}</p>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          </div>
        </section>
      ))}
    </main>
  );
}